/**
 * Financial Integrity Dashboard — Iter-251
 * =========================================
 * Single SSOT surface for the Financial Integrity Audit.
 *
 * Backed by `services.financial_integrity.provider.compute_full_report()`.
 * Same provider feeds Maintenance Runner + CLI scripts. One source of
 * truth — no duplicated logic.
 *
 * Cards rendered (top → bottom):
 *   1. Financial Health Score  (0–100, traffic light)
 *   2. Readiness Gates         (Payouts / GST Filing / Revenue Close)
 *   3. Recommended Next Action
 *   4. Financial Exposure      (GMV / Payout / Commission / GST / Refund)
 *   5. Integrity Findings      (grouped by 6 domains)
 *   6. Run History             (last 10)
 *
 * Operator Readability Rule:
 *   Affected rows surface business identifiers first (Order Number,
 *   Buyer, Vendor, Amount, AWB). Internal IDs hidden behind a
 *   "View Technical Details" expander.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  RefreshCw, AlertOctagon, AlertTriangle, Info, ShieldCheck, ChevronDown,
  ChevronRight, CheckCircle2, XCircle, Clock, ChevronsUpDown, MoreHorizontal,
  Pause, History,
} from 'lucide-react';

const ZONE_STYLES = {
  green: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', dot: 'bg-emerald-500' },
  amber: { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200',   dot: 'bg-amber-500' },
  red:   { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-200',    dot: 'bg-rose-500' },
};

const SEVERITY_STYLES = {
  critical: { cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: AlertOctagon },
  high:     { cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle },
  medium:   { cls: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: AlertTriangle },
  low:      { cls: 'bg-sky-50 text-sky-700 border-sky-200', icon: Info },
};

const GATE_ICONS = {
  ready:   { Icon: CheckCircle2, cls: 'text-emerald-600' },
  wait:    { Icon: Clock,        cls: 'text-amber-600' },
  blocked: { Icon: XCircle,      cls: 'text-rose-600' },
};

const DOMAIN_LABELS = {
  lifecycle:           'Lifecycle',
  refunds:             'Refunds',
  subscriptions:       'Subscriptions',
  payouts:             'Payouts',
  tax:                 'Tax',
  revenue_recognition: 'Revenue Recognition',
};

const formatINR = (v) => {
  const n = Number(v || 0);
  if (n === 0) return '₹0';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};
const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const SeverityBadge = ({ severity }) => {
  const s = SEVERITY_STYLES[severity] || SEVERITY_STYLES.low;
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${s.cls}`}
      data-testid={`severity-badge-${severity}`}
    >
      <Icon className="h-3 w-3" />
      {severity}
    </span>
  );
};

// ─── Health Score Card ──────────────────────────────────────────────
const HealthScoreCard = ({ score }) => {
  const value = score?.score ?? 0;
  const zone = score?.zone || 'red';
  const z = ZONE_STYLES[zone];
  const comps = score?.components || {};
  return (
    <Card data-testid="fi-health-score-card" className={`${z.bg} ring-1 ${z.ring}`}>
      <CardHeader>
        <CardDescription>Financial Health Score</CardDescription>
        <div className="flex items-baseline gap-4">
          <CardTitle className={`text-6xl font-bold ${z.text}`} data-testid="fi-health-score-value">
            {value}
            <span className="text-2xl text-slate-400 font-normal"> / 100</span>
          </CardTitle>
          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${z.text} ${z.bg}`}>
            <span className={`h-2 w-2 rounded-full ${z.dot}`} />
            {zone.toUpperCase()}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          {Object.entries({
            'Critical':  comps.critical_penalty,
            'High':      comps.high_penalty,
            'Medium':    comps.medium_penalty,
            'Exposure':  comps.exposure_penalty,
            'Freshness': comps.freshness_penalty,
          }).map(([label, pen]) => (
            <div key={label} className="bg-white/60 rounded-md p-2">
              <div className="text-slate-500 text-xs">{label} penalty</div>
              <div className="font-mono font-medium">-{Number(pen || 0).toFixed(1)}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Readiness Gates ────────────────────────────────────────────────
const ReadinessGates = ({ readiness }) => {
  const gates = readiness?.gates || [];
  return (
    <Card data-testid="fi-readiness-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Readiness Gates</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {gates.map((g) => {
            const cfg = GATE_ICONS[g.status] || GATE_ICONS.blocked;
            const Icon = cfg.Icon;
            return (
              <div
                key={g.label}
                className="rounded-md border border-slate-200 p-3 bg-white"
                data-testid={`fi-gate-${g.status}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`h-5 w-5 ${cfg.cls}`} />
                  <span className="font-medium">{g.label}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">{g.reason}</div>
                <Badge variant="secondary" className="mt-2 capitalize">{g.status}</Badge>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Recommended Next Action ────────────────────────────────────────
const NextActionCard = ({ action }) => {
  if (!action) {
    return (
      <Card data-testid="fi-next-action-card" className="border-emerald-200 bg-emerald-50/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            No actionable findings
          </CardTitle>
          <CardDescription>The system is clear — all current checks pass.</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  return (
    <Card data-testid="fi-next-action-card" className="border-rose-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Recommended Next Action</CardTitle>
          <SeverityBadge severity={action.severity} />
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div>
          <div className="text-slate-500 text-xs">Issue</div>
          <div className="font-medium">{action.title || action.code}</div>
          <div className="text-xs text-slate-400 font-mono">{action.code}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-slate-500 text-xs">Affected</div>
            <div className="font-mono">{action.count}</div>
          </div>
          <div>
            <div className="text-slate-500 text-xs">Exposure</div>
            <div className="font-mono">{formatINR(action.exposure_inr)}</div>
          </div>
        </div>
        <div>
          <div className="text-slate-500 text-xs">Recommended action</div>
          <div className="text-slate-700">{action.action}</div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Financial Exposure ─────────────────────────────────────────────
const ExposureCard = ({ exposure }) => {
  const buckets = [
    { key: 'gmv_inr',        label: 'GMV Exposure' },
    { key: 'payout_inr',     label: 'Payout Exposure' },
    { key: 'commission_inr', label: 'Commission Exposure' },
    { key: 'gst_inr',        label: 'GST Exposure' },
    { key: 'refund_inr',     label: 'Refund Exposure' },
  ];
  return (
    <Card data-testid="fi-exposure-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Financial Exposure</CardTitle>
        <CardDescription>Open ₹ at risk across categories</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {buckets.map((b) => {
            const v = Number(exposure?.[b.key] || 0);
            return (
              <div
                key={b.key}
                className={`rounded-md border p-3 ${v > 0 ? 'border-rose-200 bg-rose-50/40' : 'border-slate-200 bg-slate-50'}`}
                data-testid={`fi-exposure-${b.key}`}
              >
                <div className="text-slate-500 text-xs">{b.label}</div>
                <div className={`font-mono font-semibold ${v > 0 ? 'text-rose-700' : 'text-slate-700'}`}>
                  {formatINR(v)}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Affected Row (Operator Readability) ────────────────────────────
// Iter-251 v2 — Surfaces business identifiers first per the Operator
// Readability Rule (PRD §13). Internal IDs hidden behind "View
// Technical Details". Renders only the fields the enricher resolved
// — empty fields are skipped so operators don't see noise.
const FIELD_LABELS = {
  order_number:     'Order',
  order_date:       'Order Date',
  buyer_name:       'Buyer',
  buyer_phone:      'Phone',
  vendor_name:      'Vendor',
  amount:           'Amount',
  order_status:     'Order Status',
  payment_status:   'Payment Status',
  refund_status:    'Refund Status',
  listing_name:     'Listing',
  listing_type:     'Type',
  // iter-252 — delta / correctness fields
  current_total:    'Current Total',
  correct_total:    'Correct Total',
  inflation_amount: 'Inflation',
  awb:              'AWB',
  shipment_status:  'Shipment',
  delivered_at:     'Delivered',
  subscription_id:  'Subscription',
  billing_cycle:    'Billing Cycle',
  payout_status:    'Payout Status',
};

const STATUS_PILL = {
  // order / fulfillment
  paid:       'bg-emerald-100 text-emerald-700',
  completed:  'bg-emerald-100 text-emerald-700',
  fulfilled:  'bg-emerald-100 text-emerald-700',
  delivered:  'bg-emerald-100 text-emerald-700',
  full:       'bg-emerald-100 text-emerald-700',
  released:   'bg-emerald-100 text-emerald-700',
  // pending-ish
  pending:    'bg-amber-100 text-amber-700',
  processing: 'bg-amber-100 text-amber-700',
  in_transit: 'bg-amber-100 text-amber-700',
  partial:    'bg-amber-100 text-amber-700',
  held:       'bg-amber-100 text-amber-700',
  // negative
  cancelled:  'bg-rose-100 text-rose-700',
  failed:     'bg-rose-100 text-rose-700',
  rejected:   'bg-rose-100 text-rose-700',
  refunded:   'bg-rose-100 text-rose-700',
  // neutral
  none:       'bg-slate-100 text-slate-600',
};

const StatusPill = ({ value }) => {
  if (!value) return <span className="text-slate-400">—</span>;
  const cls = STATUS_PILL[String(value).toLowerCase()] || 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${cls}`}>
      {String(value).replace(/_/g, ' ')}
    </span>
  );
};

const FieldCell = ({ field, value }) => {
  if (value === undefined || value === null || value === '') return null;
  let rendered;
  if (field === 'amount' || field === 'current_total' || field === 'correct_total') {
    rendered = <span className="font-mono">{formatINR(value)}</span>;
  } else if (field === 'inflation_amount') {
    // iter-252 — highlight inflation in red so the WHY is unmissable.
    const n = Number(value);
    const isBad = !isNaN(n) && n > 0;
    rendered = (
      <span className={`font-mono font-medium ${isBad ? 'text-rose-600' : ''}`}>
        {isBad ? '+' : ''}{formatINR(value)}
      </span>
    );
  } else if (field === 'order_date' || field === 'delivered_at') {
    try { rendered = <span>{new Date(value).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</span>; }
    catch { rendered = <span>{String(value)}</span>; }
  } else if (['order_status', 'payment_status', 'refund_status', 'shipment_status', 'payout_status'].includes(field)) {
    rendered = <StatusPill value={value} />;
  } else if (field === 'awb' || field === 'subscription_id') {
    rendered = <span className="font-mono text-xs">{String(value)}</span>;
  } else {
    rendered = <span>{String(value)}</span>;
  }
  return (
    <div className="flex flex-col" data-testid={`fi-field-${field}`}>
      <div className="text-slate-400 text-[11px]">{FIELD_LABELS[field] || field}</div>
      <div className="text-sm">{rendered}</div>
    </div>
  );
};

// ─── Status Pill (iter-252 Finding Lifecycle) ───────────────────────
const FINDING_STATUS_STYLES = {
  open:         { cls: 'bg-rose-50 text-rose-700 border border-rose-200', label: 'Open' },
  acknowledged: { cls: 'bg-amber-50 text-amber-700 border border-amber-200', label: 'Acknowledged' },
  waived:       { cls: 'bg-slate-100 text-slate-600 border border-slate-200', label: 'Waived' },
  resolved:     { cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200', label: 'Resolved' },
};

const FindingStatusBadge = ({ status, reason, by, at }) => {
  const meta = FINDING_STATUS_STYLES[status] || FINDING_STATUS_STYLES.open;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${meta.cls}`}
      title={[reason ? `Reason: ${reason}` : null, by ? `By: ${by}` : null, at ? `At: ${new Date(at).toLocaleString('en-IN')}` : null].filter(Boolean).join('\n')}
      data-testid={`fi-row-status-${status}`}
    >
      {meta.label}
    </span>
  );
};

const AffectedRow = ({ row, audit_code, onMarkStatus }) => {
  const [open, setOpen] = useState(false);
  const d = row.display || {};
  const t = row.technical || {};
  const status = row.status || 'open';
  // iter-252 — Render fields in a stable visual order. Delta fields
  // (current/correct/inflation) live alongside listing for at-a-glance
  // comprehension of "what's wrong by how much".
  const order = [
    'order_number', 'order_date', 'vendor_name', 'buyer_name', 'buyer_phone',
    'listing_name', 'listing_type',
    'current_total', 'correct_total', 'inflation_amount',
    'amount',
    'order_status', 'payment_status', 'refund_status',
    'awb', 'shipment_status', 'delivered_at',
    'subscription_id', 'billing_cycle', 'payout_status',
  ];
  const presentFields = order.filter((k) => d[k] !== undefined && d[k] !== null && d[k] !== '');
  // For the "Mark as…" menu we need stable identity. Provider attaches
  // these as row.type and row.id (entity_type + entity_id).
  const entity_type = row.type || (t.shipment_id ? 'shipment' : t.order_id ? 'order' : null);
  const entity_id = row.id || t.order_id || t.shipment_id || t.refund_id || null;
  const canMark = Boolean(audit_code && entity_type && entity_id);
  return (
    <div
      className={`border-b border-slate-100 last:border-0 py-2.5 ${
        status === 'waived' || status === 'resolved' ? 'opacity-70' : ''
      }`}
      data-testid="fi-affected-row"
      data-status={status}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {presentFields.length === 0 ? (
            <div className="text-xs text-slate-400 italic py-1" data-testid="fi-affected-row-unresolved">
              Could not resolve order/vendor context for this row. Open Technical Details below.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-3 gap-y-2">
              {presentFields.map((k) => (
                <FieldCell key={k} field={k} value={d[k]} />
              ))}
            </div>
          )}
        </div>
        <div className="flex items-start gap-1.5 shrink-0">
          <FindingStatusBadge
            status={status}
            reason={row.status_reason}
            by={row.status_by}
            at={row.status_at}
          />
          {canMark && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-slate-100 text-slate-500"
                  data-testid="fi-row-actions-trigger"
                  aria-label="Row actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-xs">Mark as…</DropdownMenuLabel>
                <DropdownMenuItem
                  disabled={status === 'acknowledged'}
                  onClick={() => onMarkStatus?.({ row, audit_code, entity_type, entity_id, intent: 'acknowledged' })}
                  data-testid="fi-row-mark-ack"
                >
                  Acknowledged
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={status === 'waived'}
                  onClick={() => onMarkStatus?.({ row, audit_code, entity_type, entity_id, intent: 'waived' })}
                  data-testid="fi-row-mark-waive"
                >
                  Waive (with reason)…
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={status === 'resolved'}
                  onClick={() => onMarkStatus?.({ row, audit_code, entity_type, entity_id, intent: 'resolved' })}
                  data-testid="fi-row-mark-resolve"
                >
                  Resolve (with reason)…
                </DropdownMenuItem>
                {status !== 'open' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onMarkStatus?.({ row, audit_code, entity_type, entity_id, intent: 'open', reopen: true })}
                      data-testid="fi-row-mark-reopen"
                    >
                      Re-open
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {(status === 'waived' || status === 'resolved') && row.status_reason && (
        <div className="mt-1.5 text-[11px] text-slate-500 italic" data-testid="fi-row-status-reason">
          <span className="font-medium">{status === 'waived' ? 'Waived' : 'Resolved'}:</span>{' '}
          {row.status_reason}
          {row.status_by ? ` — ${row.status_by}` : ''}
          {row.status_at ? ` · ${new Date(row.status_at).toLocaleDateString('en-IN', { dateStyle: 'medium' })}` : ''}
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="mt-2 text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
        data-testid="fi-affected-row-toggle-technical"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        View Technical Details
      </button>
      {open && (
        <div className="mt-1 ml-4 bg-slate-50 rounded p-2 font-mono text-xs">
          {Object.entries(t).length === 0 ? (
            <div className="text-slate-400">No technical IDs available.</div>
          ) : (
            Object.entries(t).map(([k, v]) => (
              <div key={k}><span className="text-slate-500">{k}:</span> {String(v)}</div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─── Findings By Domain ─────────────────────────────────────────────
const FindingsByDomain = ({ findingsByDomain, onMarkStatus, showWaivedResolved, onToggleShowWaived }) => {
  const [openCode, setOpenCode] = useState(null);
  const domains = useMemo(
    () => Object.keys(DOMAIN_LABELS),
    [],
  );

  // iter-252 — `count_open` (waived/resolved excluded) drives badges /
  // sort order. Raw `count` still shown as "Total" so operators see the
  // historic detection volume.
  const _openCount = (f) => (
    f.count_open !== undefined && f.count_open !== null
      ? Number(f.count_open) : Number(f.count || 0)
  );

  return (
    <Card data-testid="fi-findings-card">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base">Integrity Findings</CardTitle>
            <CardDescription>
              Grouped by domain. Click a row to view operator-readable affected items.
              Health Score &amp; Readiness Gates exclude waived/resolved rows.
            </CardDescription>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="fi-show-waived-toggle-wrap">
            <span>Show waived/resolved</span>
            <Switch
              checked={!!showWaivedResolved}
              onCheckedChange={(v) => typeof onToggleShowWaived === 'function' && onToggleShowWaived(v)}
              data-testid="fi-show-waived-toggle"
            />
          </label>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="refunds" data-testid="fi-findings-tabs">
          <TabsList className="flex flex-wrap">
            {domains.map((d) => {
              const count = (findingsByDomain?.[d] || []).reduce(
                (a, f) => a + (_openCount(f) > 0 ? 1 : 0), 0,
              );
              return (
                <TabsTrigger key={d} value={d} data-testid={`fi-tab-${d}`}>
                  {DOMAIN_LABELS[d]}
                  {count > 0 && <Badge variant="secondary" className="ml-2">{count}</Badge>}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {domains.map((d) => {
            // iter-252 Phase C — ALWAYS render every finding in the
            // domain, even at count=0. Operators see the audit ran.
            const findings = findingsByDomain?.[d] || [];
            return (
              <TabsContent key={d} value={d}>
                {findings.length === 0 ? (
                  <div className="text-sm text-slate-500 py-4 text-center" data-testid={`fi-empty-${d}`}>
                    No checks registered for this domain.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-1/2">Finding</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead className="text-right">Open</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Exposure</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {findings.map((f) => {
                        const openN = _openCount(f);
                        const totalN = Number(f.count || 0);
                        // Use OPEN-only exposure (provider computes it).
                        const expo = (
                          (f.payout_impact_open ?? f.payout_impact ?? 0)
                          + (f.commission_impact_open ?? f.commission_impact ?? 0)
                          + (f.gst_impact_open ?? f.gst_impact ?? 0)
                          + (f.gmv_impact_open ?? f.gmv_impact ?? 0)
                        );
                        const isOpen = openCode === f.code;
                        const isCleared = totalN > 0 && openN === 0;  // all waived/resolved
                        const isClean = totalN === 0;
                        // Filter rows based on the show-waived/resolved toggle.
                        const rows = (f.affected_ids_enriched || []).filter((r) => {
                          if (showWaivedResolved) return true;
                          const s = r.status || 'open';
                          return s !== 'waived' && s !== 'resolved';
                        });
                        return (
                          <>
                            <TableRow
                              key={f.code}
                              className={`cursor-pointer hover:bg-slate-50 ${isClean ? 'opacity-60' : ''}`}
                              onClick={() => setOpenCode(isOpen ? null : f.code)}
                              data-testid={`fi-finding-row-${f.code}`}
                            >
                              <TableCell>
                                <div className="font-medium flex items-center gap-2">
                                  <span>{f.title || f.code}</span>
                                  {isClean && (
                                    <Badge variant="outline" className="text-[10px] py-0 px-1 border-emerald-200 text-emerald-700 bg-emerald-50" data-testid={`fi-clean-${f.code}`}>
                                      No issues detected
                                    </Badge>
                                  )}
                                  {isCleared && (
                                    <Badge variant="outline" className="text-[10px] py-0 px-1 border-slate-200 text-slate-600 bg-slate-50" data-testid={`fi-cleared-${f.code}`}>
                                      Cleared
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-slate-400 font-mono">{f.code}</div>
                              </TableCell>
                              <TableCell><SeverityBadge severity={f.severity} /></TableCell>
                              <TableCell className={`text-right font-mono ${openN > 0 ? '' : 'text-slate-400'}`} data-testid={`fi-row-open-${f.code}`}>
                                {openN}
                              </TableCell>
                              <TableCell className="text-right font-mono text-slate-500" data-testid={`fi-row-total-${f.code}`}>
                                {totalN}
                              </TableCell>
                              <TableCell className="text-right font-mono">{formatINR(expo)}</TableCell>
                              <TableCell>
                                <ChevronsUpDown className="h-4 w-4 text-slate-400" />
                              </TableCell>
                            </TableRow>
                            {isOpen && (
                              <TableRow>
                                <TableCell colSpan={6} className="bg-slate-50/60 p-3">
                                  {f.fix_hint && (
                                    <div className="text-sm text-slate-600 mb-3">
                                      <span className="font-medium">Fix hint: </span>{f.fix_hint}
                                    </div>
                                  )}
                                  <div className="text-xs text-slate-500 mb-2 flex items-center gap-3">
                                    <span>
                                      Affected rows ({rows.length}
                                      {(!showWaivedResolved && (f.status_breakdown?.waived || 0) + (f.status_breakdown?.resolved || 0) > 0)
                                        ? ` shown · ${(f.status_breakdown?.waived || 0) + (f.status_breakdown?.resolved || 0)} hidden`
                                        : ''}):
                                    </span>
                                  </div>
                                  {(f.affected_ids_enriched || []).length === 0 ? (
                                    <div className="text-xs text-slate-400" data-testid={`fi-no-rows-${f.code}`}>
                                      {totalN === 0
                                        ? 'No issues detected — audit ran and produced zero findings for this check.'
                                        : 'No sample rows enriched (this finding source does not provide drill-down details).'}
                                    </div>
                                  ) : rows.length === 0 ? (
                                    <div className="text-xs text-slate-400" data-testid={`fi-all-cleared-${f.code}`}>
                                      All rows for this check are waived or resolved. Toggle &ldquo;Show waived/resolved&rdquo; above to see them.
                                    </div>
                                  ) : (
                                    <div className="bg-white rounded border border-slate-200 px-3">
                                      {rows.map((row, i) => (
                                        <AffectedRow
                                          key={`${row.id || i}`}
                                          row={row}
                                          audit_code={f.code}
                                          onMarkStatus={onMarkStatus}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </CardContent>
    </Card>
  );
};

// ─── Run History ────────────────────────────────────────────────────
const RunHistoryCard = ({ runs }) => (
  <Card data-testid="fi-runs-card">
    <CardHeader className="pb-2">
      <CardTitle className="text-base">Run History</CardTitle>
      <CardDescription>Last 10 runs across scheduled + manual triggers</CardDescription>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Completed</TableHead>
            <TableHead>Trigger</TableHead>
            <TableHead className="text-right">Score</TableHead>
            <TableHead className="text-right">Critical</TableHead>
            <TableHead className="text-right">Total Exposure</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(runs || []).length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-sm text-slate-500 py-3" data-testid="fi-runs-empty">
                No persisted runs yet — trigger a refresh.
              </TableCell>
            </TableRow>
          ) : (
            runs.map((r) => {
              const exposureTotal = (
                (r.exposure?.payout_inr || 0)
                + (r.exposure?.commission_inr || 0)
                + (r.exposure?.gst_inr || 0)
                + (r.exposure?.refund_inr || 0)
              );
              const zone = r.health_score?.zone || 'red';
              return (
                <TableRow key={r.run_id} data-testid={`fi-run-row-${r.run_id}`}>
                  <TableCell className="text-sm">{formatDate(r.completed_at)}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="capitalize">{r.triggered_by || 'manual'}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    <span className={ZONE_STYLES[zone]?.text}>{r.health_score?.score ?? '—'}</span>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {r.severity_counts?.critical ?? 0}
                  </TableCell>
                  <TableCell className="text-right font-mono">{formatINR(exposureTotal)}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

// ─── Mark-Status Dialog (iter-252) ──────────────────────────────────
const STATUS_DIALOG_META = {
  acknowledged: { title: 'Acknowledge finding', verb: 'acknowledge', reasonRequired: false, btn: 'Acknowledge' },
  waived:       { title: 'Waive finding',       verb: 'waive',       reasonRequired: true,  btn: 'Waive' },
  resolved:     { title: 'Resolve finding',     verb: 'resolve',     reasonRequired: true,  btn: 'Resolve' },
  open:         { title: 'Re-open finding',     verb: 're-open',     reasonRequired: false, btn: 'Re-open' },
};

const MarkStatusDialog = ({ context, onClose, onSubmit, submitting }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { setReason(''); }, [context?.intent, context?.entity_id]);
  if (!context) return null;
  const meta = STATUS_DIALOG_META[context.intent] || STATUS_DIALOG_META.acknowledged;
  const reasonInvalid = meta.reasonRequired && reason.trim().length < 4;
  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (reasonInvalid) return;
    onSubmit({ ...context, reason: reason.trim() });
  };
  return (
    <Dialog open={!!context} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="fi-status-dialog">
        <DialogHeader>
          <DialogTitle>{meta.title}</DialogTitle>
          <DialogDescription>
            Mark <code className="font-mono">{context.audit_code}</code> on{' '}
            <span className="font-medium">{context.row?.display?.order_number || context.entity_id}</span> as{' '}
            <span className="font-medium">{meta.verb}</span>. History is append-only — every change is recorded.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">
              Reason {meta.reasonRequired ? <span className="text-rose-500">*</span> : <span className="text-slate-400">(optional)</span>}
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                context.intent === 'waived'
                  ? 'e.g. Refunded offline. Reconciled manually. Historical subscription migration artifact.'
                  : context.intent === 'resolved'
                  ? 'e.g. Root cause fixed in commit abc123. Backfill complete.'
                  : 'Optional context for the audit log.'
              }
              rows={3}
              className="mt-1"
              data-testid="fi-status-reason"
              autoFocus
            />
            {meta.reasonRequired && reason.trim().length > 0 && reason.trim().length < 4 && (
              <div className="text-[11px] text-rose-500 mt-1">Reason must be at least 4 characters.</div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="fi-status-cancel">Cancel</Button>
            <Button
              type="submit"
              disabled={reasonInvalid || submitting}
              data-testid="fi-status-submit"
            >
              {submitting ? 'Saving…' : meta.btn}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Page ──────────────────────────────────────────────────────
export default function AdminFinancialIntegrity() {
  const { apiCall } = useAuth();
  const [report, setReport] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // iter-252 — Finding lifecycle UX state.
  const [showWaivedResolved, setShowWaivedResolved] = useState(false);
  const [statusDialog, setStatusDialog] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    try {
      const [r, h] = await Promise.all([
        apiCall('/api/admin/financial-integrity/report/latest'),
        apiCall('/api/admin/financial-integrity/runs?limit=10'),
      ]);
      setReport(r);
      setRuns(h?.runs || []);
    } catch (e) {
      toast.error(`Failed to load Financial Integrity report: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // Synchronous refresh — keeps the UX simple for one-off operator runs.
      // Daily scheduled run handles regular cadence.
      await apiCall('/api/admin/financial-integrity/report/refresh?sync=1', { method: 'POST' });
      toast.success('Financial Integrity report refreshed');
      await fetchLatest();
    } catch (e) {
      toast.error(`Refresh failed: ${e.message || e}`);
    } finally {
      setRefreshing(false);
    }
  }, [apiCall, fetchLatest]);

  // iter-252 — Open the dialog (waive/resolve require a reason). For
  // `acknowledged` and `open` (reopen) we still surface the dialog so
  // operators can optionally annotate the change.
  const handleMarkStatus = useCallback((ctx) => {
    setStatusDialog(ctx);
  }, []);

  const submitStatus = useCallback(async ({ audit_code, entity_type, entity_id, intent, reason, row }) => {
    setStatusSubmitting(true);
    try {
      await apiCall('/api/admin/financial-integrity/findings/status', {
        method: 'POST',
        body: JSON.stringify({
          audit_code,
          entity_type,
          entity_id,
          status: intent,
          reason: reason || null,
          display_order_number: row?.display?.order_number || null,
        }),
      });
      toast.success(`Marked as ${intent}`);
      setStatusDialog(null);
      // Refresh the report so health/gates/exposure recompute.
      await handleRefresh();
    } catch (e) {
      toast.error(`Failed to update status: ${e.message || e}`);
    } finally {
      setStatusSubmitting(false);
    }
  }, [apiCall, handleRefresh]);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  return (
    <DashboardLayout>
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="financial-integrity-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Financial Integrity</h1>
          <p className="text-sm text-slate-500 mt-1">
            One source of truth for refund leakage, payout snapshot coverage, lifecycle alignment,
            and audit hygiene. Daily auto-run at 02:00 IST. SSOT engine: <code className="text-xs">services.financial_integrity.provider</code>.
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing || loading}
          data-testid="fi-refresh-button"
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh now'}
        </Button>
      </header>

      {loading && !report && (
        <div className="text-sm text-slate-500" data-testid="fi-loading">Loading Financial Integrity report…</div>
      )}

      {report && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <HealthScoreCard score={report.health_score} />
            <NextActionCard action={report.next_action} />
          </div>
          <ReadinessGates readiness={report.readiness} />
          <ExposureCard exposure={report.exposure} />
          <FindingsByDomain
            findingsByDomain={report.findings_by_domain}
            onMarkStatus={handleMarkStatus}
            showWaivedResolved={showWaivedResolved}
            onToggleShowWaived={setShowWaivedResolved}
          />
          <RunHistoryCard runs={runs} />
          <div className="text-xs text-slate-400 text-right">
            Last computed: {formatDate(report.completed_at)} · run_id: <span className="font-mono">{report.run_id}</span>
          </div>
        </>
      )}

      <MarkStatusDialog
        context={statusDialog}
        onClose={() => setStatusDialog(null)}
        onSubmit={submitStatus}
        submitting={statusSubmitting}
      />
    </div>
    </DashboardLayout>
  );
}
